import React, { useContext, useState } from 'react'
import { AppContext } from '@state/context'
import { closePanel } from '@state/actions/panelsActions'
import Button from '@components/Button'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  TEXT_SIZES,
  STUDENT_PROGRESS_STATUS,
  COLORS,
} from '@helpers/constants'
import Text from '@components/Text'
import Icon from '@components/Icon'
import { clearProgressInspectionStuff } from '@state/actions/appActions'
import {
  getStudentAttemptsToDelete,
  getStudentsByBookStatus,
  getStudentsBySectionStatus,
} from '@helpers/exams'
import { buildStudentLabel } from '@helpers/students'
import { useConfirm } from '@hooks/useConfirm'
import Loader from '@components/Loader'
import { useDeleteExamAttempts } from '@hooks/mutations/useDeleteExamAttempts'

const InspectProgressPanel = () => {
  const [state, dispatch] = useContext(AppContext)
  const {
    app: {
      selectedBookForProgressInspection,
      selectedSectionForProgressInspection,
      selectedSchoolResourceForProgressInspection,
      selectedInspectionStatus,
    },
  } = state

  let name = ''

  const allStudents =
    selectedSchoolResourceForProgressInspection?.students ?? []

  let studentsNotStarted = []
  let studentsInProgress = []
  let studentsFailed = []
  let studentsPassed = []

  let studentsToShow = []

  if (selectedBookForProgressInspection) {
    name = selectedBookForProgressInspection.name
    const sections = selectedBookForProgressInspection.sections

    studentsNotStarted = getStudentsByBookStatus(
      sections,
      allStudents,
      STUDENT_PROGRESS_STATUS.NOT_STARTED
    )
    studentsInProgress = getStudentsByBookStatus(
      sections,
      allStudents,
      STUDENT_PROGRESS_STATUS.IN_PROGRESS
    )
    studentsFailed = getStudentsByBookStatus(
      sections,
      allStudents,
      STUDENT_PROGRESS_STATUS.FAILED
    )
    studentsPassed = getStudentsByBookStatus(
      sections,
      allStudents,
      STUDENT_PROGRESS_STATUS.PASSED
    )
  } else if (selectedSectionForProgressInspection) {
    name = selectedSectionForProgressInspection.name

    studentsNotStarted = getStudentsBySectionStatus(
      selectedSectionForProgressInspection,
      allStudents,
      STUDENT_PROGRESS_STATUS.NOT_STARTED
    )
    studentsInProgress = getStudentsBySectionStatus(
      selectedSectionForProgressInspection,
      allStudents,
      STUDENT_PROGRESS_STATUS.IN_PROGRESS
    )
    studentsFailed = getStudentsBySectionStatus(
      selectedSectionForProgressInspection,
      allStudents,
      STUDENT_PROGRESS_STATUS.FAILED
    )
    studentsPassed = getStudentsBySectionStatus(
      selectedSectionForProgressInspection,
      allStudents,
      STUDENT_PROGRESS_STATUS.PASSED
    )
  }

  if (selectedInspectionStatus === STUDENT_PROGRESS_STATUS.NOT_STARTED) {
    studentsToShow = studentsNotStarted
  }
  if (selectedInspectionStatus === STUDENT_PROGRESS_STATUS.IN_PROGRESS) {
    studentsToShow = studentsInProgress
  }
  if (selectedInspectionStatus === STUDENT_PROGRESS_STATUS.FAILED) {
    studentsToShow = studentsFailed
  }
  if (selectedInspectionStatus === STUDENT_PROGRESS_STATUS.PASSED) {
    studentsToShow = studentsPassed
  }

  let icon = ''
  let iconColor = undefined
  let iconClasses = ''

  let text = ''

  if (selectedBookForProgressInspection) {
    // --- Book
    iconClasses = 'w-[72px] h-[72px]'

    if (selectedInspectionStatus === STUDENT_PROGRESS_STATUS.NOT_STARTED) {
      icon = 'SectionCircleNoAttempts'
      iconColor = COLORS.BLACK.TEXT
      text =
        studentsToShow.length > 0
          ? 'Följande elever <b>har inte påbörjat</b> denna bok:'
          : 'Alla elever <b>har påbörjat</b> denna bok.'
    }

    if (selectedInspectionStatus === STUDENT_PROGRESS_STATUS.IN_PROGRESS) {
      icon = 'SectionCircleHasAttempts'
      text =
        studentsToShow.length > 0
          ? 'Följande elever <b>arbetar</b> med denna bok:'
          : 'Inga elever <b>arbetar</b> med denna bok.'
    }

    if (selectedInspectionStatus === STUDENT_PROGRESS_STATUS.FAILED) {
      icon = 'SectionCircleFailedCross'
      text =
        studentsToShow.length > 0
          ? 'Följande elever har fått <b>icke godkänt</b> på denna bok:'
          : 'Inga elever har fått <b>icke godkänt</b> på denna bok.'
    }

    if (selectedInspectionStatus === STUDENT_PROGRESS_STATUS.PASSED) {
      icon = 'SectionCirclePassedCheckmark'
      text =
        studentsToShow.length > 0
          ? 'Följande elever har fått <b>godkänt</b> på denna bok:'
          : 'Inga elever har fått <b>godkänt</b> på denna bok.'
    }
  } else if (selectedSectionForProgressInspection) {
    // --- Section
    iconClasses = 'w-[60px] h-[72px]'

    if (selectedInspectionStatus === STUDENT_PROGRESS_STATUS.NOT_STARTED) {
      icon = 'SectionTagHasExamsNoAttempts'
      iconColor = COLORS.BLACK.TEXT
      text =
        studentsToShow.length > 0
          ? 'Följande elever <b>har inte påbörjat</b> detta avsnitt:'
          : 'Alla elever <b>har påbörjat</b> detta avsnitt.'
    }

    if (selectedInspectionStatus === STUDENT_PROGRESS_STATUS.IN_PROGRESS) {
      icon = 'SectionTagHasExamsHasAttempts'
      text =
        studentsToShow.length > 0
          ? 'Följande elever <b>arbetar</b> med detta avsnitt:'
          : 'Inga elever <b>arbetar</b> med detta avsnitt.'
    }

    if (selectedInspectionStatus === STUDENT_PROGRESS_STATUS.FAILED) {
      icon = 'SectionTagFailedCross'
      text =
        studentsToShow.length > 0
          ? 'Följande elever har fått <b>icke godkänt</b> på detta avsnitt:<br />Du kan ta bort elevers försök nedan.'
          : 'Inga elever har fått <b>icke godkänt</b> på detta avsnitt.'
    }

    if (selectedInspectionStatus === STUDENT_PROGRESS_STATUS.PASSED) {
      icon = 'SectionTagPassedCheckmark'
      text =
        studentsToShow.length > 0
          ? 'Följande elever har fått <b>godkänt</b> på detta avsnitt:'
          : 'Inga elever har fått <b>godkänt</b> på detta avsnitt.'
    }
  }

  return (
    <>
      <div className={'flex justify-between items-center'}>
        <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.MEDIUM}>
          {name}
        </Heading>

        <Button
          handleClick={() => {
            clearProgressInspectionStuff()(dispatch)
            dispatch(closePanel(PANELS.INSPECT_PROGRESS))
          }}
          size={BUTTON_SIZE.NO_PADDING}
          icon={'Close'}
        ></Button>
      </div>
      <Divider size={DIVIDER_SIZE.SMALL} />
      <Divider size={DIVIDER_SIZE.LINE} line />

      <Icon
        icon={icon}
        color={iconColor}
        iconClasses={`my-12 mx-auto ${iconClasses}`}
      />

      <p
        dangerouslySetInnerHTML={{
          __html: text,
        }}
      />

      <Divider size={DIVIDER_SIZE.SMALL} />

      <ul>
        {studentsToShow.map((student, index) => {
          const { id } = student
          const label = buildStudentLabel(student, allStudents)

          return (
            <li key={`${id}-${index}`}>
              <StudentItem
                label={label}
                student={student}
                selectedSectionForProgressInspection={
                  selectedSectionForProgressInspection
                }
                selectedInspectionStatus={selectedInspectionStatus}
              />

              {index < studentsToShow.length - 1 && (
                <>
                  <Divider size={DIVIDER_SIZE.XXSMALL} line />
                  <Divider size={DIVIDER_SIZE.XXSMALL} />
                </>
              )}
            </li>
          )
        })}
      </ul>
    </>
  )
}

const StudentItem = (props) => {
  const {
    label,
    student,
    selectedSectionForProgressInspection,
    selectedInspectionStatus,
  } = props

  const { confirm } = useConfirm()

  const { post: postDelete, isPosting: isPostingDelete } =
    useDeleteExamAttempts()

  const confirmDeleteAttempts = async () => {
    const studentAttemptsToDelete = getStudentAttemptsToDelete(
      student.id,
      selectedSectionForProgressInspection
    )

    const confirmed = await confirm({
      text: `Är du säker på att du vill nollställa avsnittet för ${label}?`,
      // additionalText: 'additionalText.',
      confirmText: 'Ja, ta bort',
      cancelText: 'Avbryt',
    })

    if (confirmed) {
      try {
        await postDelete(
          {
            ids: studentAttemptsToDelete,
          },
          'Avsnittet nollställdes'
        )
      } catch (error) {
        console.log('🚀 ~ DeleteExamAttempts ~ error:', error)
      }
    }
  }

  const deleteAttemptsAllowed =
    selectedSectionForProgressInspection &&
    selectedInspectionStatus === STUDENT_PROGRESS_STATUS.FAILED

  return (
    <div
      className={`flex items-center justify-between rounded-full py-1 px-4 -mx-4 ${
        deleteAttemptsAllowed ? 'hover:bg-secondaryGreen' : ''
      }`}
    >
      <Text>{label}</Text>

      {/* Delete all attempts on all exams on section */}
      {deleteAttemptsAllowed && (
        <div className='flex items-center'>
          {isPostingDelete && <Loader noMargin />}
          <Button
            size={BUTTON_SIZE.NO_PADDING}
            color={COLORS.BLACK.TEXT}
            icon={'UndoArrow'}
            handleClick={() => {
              if (!isPostingDelete) {
                confirmDeleteAttempts()
              }
            }}
          ></Button>
        </div>
      )}
    </div>
  )
}

export default InspectProgressPanel
